import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { PortOvertimeLevel } from '../../../../shared/enums/port-overtime-level.enum';
import { PortOvertimeRuleModel } from '../../../../shared/models/port-overtime-rule.model';
import { PortOvertimeRuleFormService } from '../port-overtime-rule-form.service';

// A day always has 1440 minutes (24h × 60min) — used to convert a "HH:mm" time into a
// position/size percentage on the day bar (e.g. 19:00 → minute 1140 → 79% down the bar).
const MINUTES_IN_DAY = 1440;

// Monday = 1 … Sunday = 7, matching the API's ISO daysOfWeek.
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

// Hour gridlines drawn inside each day bar. 1..23 only — hour 0 and hour 24 are just the
// bar's own top and bottom edges, they don't need a line drawn on top of them.
const HOUR_GRIDLINES = Array.from({ length: 23 }, (_, index) => index + 1);

// Hour labels on the left axis: every hour, 0 through 24 (both edges included).
const HOUR_MARKS = Array.from({ length: 25 }, (_, index) => index);

// One colored block drawn on a single day's bar.
interface DaySegment {
  ruleId: string;
  level: PortOvertimeLevel | string;
  startMinute: number;
  endMinute: number;
  // Segments that continue onto the next day (see splitIntoSegments below) get a small
  // wrap icon and a highlighted border, as a visual hint that the rule doesn't stop there.
  crossesMidnight: boolean;
}

/**
 * Weekly clock/grid view of a port's overtime rules: one vertical 24h bar per day,
 * with OT1/OT2 rules painted as colored blocks on top of it.
 *
 * This component only turns `rules` into something visual — it doesn't fetch data or
 * open the edit form itself. It just tells its parent which rule was clicked via
 * `ruleClick`, and the parent decides what to do about it.
 */
@Component({
  selector: 'app-overtime-clock',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  templateUrl: './overtime-clock.html',
  styleUrl: './overtime-clock.css',
})
export class OvertimeClock {
  readonly Level = PortOvertimeLevel;
  readonly weekdays = WEEKDAYS;
  readonly hourGridlines = HOUR_GRIDLINES;
  readonly hourMarks = HOUR_MARKS;

  private readonly formService = inject(PortOvertimeRuleFormService);

  rules = input.required<PortOvertimeRuleModel[]>();
  ruleClick = output<string>();

  // One list of segments per weekday, ready to render — computed once here so the
  // template stays a plain @for loop with no logic in it.
  segmentsByDay = computed<Record<number, DaySegment[]>>(() => this.groupSegmentsByDay(this.rules()));

  segmentsForDay(day: number): DaySegment[] {
    return this.segmentsByDay()[day] ?? [];
  }

  dayLabel(day: number): string {
    return this.formService.dayLabel(day);
  }

  // Converts a segment's start/end minute into a CSS top/height percentage, so it can be
  // positioned inside its day's bar with a plain [style] binding.
  segmentStyle(segment: DaySegment): Record<string, string> {
    const top = (segment.startMinute / MINUTES_IN_DAY) * 100;
    const height = ((segment.endMinute - segment.startMinute) / MINUTES_IN_DAY) * 100;
    return { top: `${top}%`, height: `${height}%` };
  }

  // "19:00–22:00" — shown centered inside the segment itself so the exact times are
  // readable without having to hover for the tooltip.
  segmentLabel(segment: DaySegment): string {
    return `${this.formatMinutes(segment.startMinute)}–${this.formatMinutes(segment.endMinute)}`;
  }

  onSegmentClick(ruleId: string): void {
    this.ruleClick.emit(ruleId);
  }

  private groupSegmentsByDay(rules: PortOvertimeRuleModel[]): Record<number, DaySegment[]> {
    const byDay: Record<number, DaySegment[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };

    for (const rule of rules) {
      if (!rule.id || !rule.startTime || !rule.endTime) {
        continue;
      }

      for (const day of rule.daysOfWeek) {
        if (byDay[day]) {
          this.addSegmentsForDay(byDay, day, rule);
        }
      }
    }

    for (const day of this.weekdays) {
      byDay[day].sort((left, right) => left.startMinute - right.startMinute);
    }

    return byDay;
  }

  // Adds the segment(s) for one rule on one day. Most rules add a single segment to
  // their own day. A rule that crosses midnight (endTime <= startTime, e.g. Friday
  // 19:00–07:00) adds two: the part running until 24:00 on `day`, and the continuation
  // from 00:00 on the next day — so the "wrap" is visible on both bars instead of just
  // disappearing.
  private addSegmentsForDay(byDay: Record<number, DaySegment[]>, day: number, rule: PortOvertimeRuleModel): void {
    const ruleId = String(rule.id);
    const level = rule.level ?? '';
    const startMinute = this.toMinutes(rule.startTime!);
    const endMinute = this.toMinutes(rule.endTime!);

    if (!rule.crossesMidnight()) {
      byDay[day].push({ ruleId, level, startMinute, endMinute, crossesMidnight: false });
      return;
    }

    byDay[day].push({ ruleId, level, startMinute, endMinute: MINUTES_IN_DAY, crossesMidnight: true });

    const nextDay = day === 7 ? 1 : day + 1;
    byDay[nextDay].push({ ruleId, level, startMinute: 0, endMinute, crossesMidnight: true });
  }

  // "HH:mm" → minutes since midnight. "24:00" is the one allowed value past 23:59, and
  // it always means "end of day" (minute 1440), not hour 24 of a new day.
  private toMinutes(time: string): number {
    if (time === '24:00') {
      return MINUTES_IN_DAY;
    }
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Minutes since midnight → "HH:mm" (the reverse of toMinutes), so a split midnight-
  // crossing segment (which no longer has its own "HH:mm" string) can still show one.
  private formatMinutes(minutes: number): string {
    if (minutes === MINUTES_IN_DAY) {
      return '24:00';
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`;
  }
}
